import {
  Avatar,
  IconButton,
  Tooltip,
  makeStyles,
  Typography,
} from "@material-ui/core";
import SalesPersonDetailDialog from "./SalesPersonDetailDialog";
import MaterialTable, { Column } from "material-table";
import { useState } from "react";
import DeleteIcon from "@material-ui/icons/Delete";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import CancelIcon from "@material-ui/icons/Cancel";
import {
  useDeleteSalesPerson,
  useGetSalesPerson,
  useApproveSalesPerson,
  useActivateSalesPerson,
} from "../../../../../../utils/react-query/querySalesperson";
import DeleteConfirmationDialog from "../../../../../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { materialTableES } from "../../../../../../services/materialTableES";

const useStyles = makeStyles((theme) => ({
  avatarBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  email: {
    fontSize: "0.9rem",
    fontWeight: 400,
    color: "#919EAB",
  },
  name: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#1c252e",
    fontFamily: "inherit",
  },
  link: {
    color: "#1976D2",
    textDecoration: "none",
  },
  linksList: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  iconBtn: {
    padding: 4,
    transition: "background-color 120ms ease, transform 120ms ease",
    "&:hover": {
      transform: "scale(1.05)",
    },
  },
  approved: {
    color: "#00AB55",
    "&:hover": { backgroundColor: "rgba(0,171,85,0.08)" },
  },
  rejected: {
    color: "#FF5630",
    "&:hover": { backgroundColor: "rgba(255,86,48,0.08)" },
  },
}));

function SalesPersonTable() {
  const classes = useStyles();
  const { data: salesPeople = [], isLoading: isLoadingSalesPeople } =
    useGetSalesPerson();
  const { mutateAsync: deleteSalesPerson } = useDeleteSalesPerson();
  const { mutateAsync: approveSalesPerson } = useApproveSalesPerson();
  const { mutateAsync: activateSalesPerson } = useActivateSalesPerson();

  const handleToggleApproved = async (row: SalesPeople) => {
    await approveSalesPerson({
      salesPersonId: row.salesperson_id,
      salesPersonData: { is_approved: !row.is_approved },
    });
  };

  const handleToggleActive = async (row: SalesPeople) => {
    await activateSalesPerson({
      salesPersonId: row.salesperson_id,
      salesPersonData: { is_public: !row.is_public },
    });
  };

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    onConfirm: () => {},
  });

  const [selected, setSelected] = useState<SalesPeople | null>(null);
  const [openDetail, setOpenDetail] = useState(false);
  const handleOpenDetails = (row: SalesPeople) => {
    setSelected(row);
    setOpenDetail(true);
  };
  const handleCloseDetails = () => setOpenDetail(false);

  const handleDeleteSalesPerson = async (salespersonId: number) => {
    await deleteSalesPerson(salespersonId);
  };

  const toLookup = (values: Array<string | null | undefined>) => {
    const valueLookup: Record<string, string> = {};
    values.forEach((value) => {
      const newValue = (value || "").trim();
      if (newValue) valueLookup[newValue] = newValue;
    });
    return valueLookup;
  };
  const list: SalesPeople[] = Array.isArray(salesPeople)
    ? (salesPeople as SalesPeople[])
    : [];
  const cityLookup = toLookup(list.map((salesperson) => salesperson.city_name));
  const profileLookup = toLookup(
    list.map((salesperson) => salesperson.professional_profile)
  );

  const columns: Column<SalesPeople>[] = [
    {
      title: "Avatar",
      field: "avatar",
      width: "10%",
      filtering: false,
      render: (rowData) => {
        if (!rowData.avatar_list || rowData.avatar_list.length === 0) {
          return null;
        }
        return (
          <Avatar alt={rowData.first_name} src={rowData.avatar_list[0].url} />
        );
      },
    },
    {
      title: "Nombre",
      field: "first_name",
      filtering: false,
      render: (rowData) => {
        return (
          <Typography
            className={classes.name}
          >{`${rowData.first_name} ${rowData.last_name}`}</Typography>
        );
      },
    },
    {
      title: "Correo",
      field: "email",
      editable: "never",
      filtering: false,
    },
    {
      title: "Ciudad",
      field: "city_name",
      editable: "never",
      filtering: true,
      lookup: cityLookup,
    },
    {
      title: "Perfil Profesional",
      field: "professional_profile",
      editable: "never",
      filtering: true,
      lookup: profileLookup,
    },
    {
      title: "Aprobado",
      field: "is_approved",
      filtering: false,
      render: (rowData) => (
        <Tooltip title={rowData.is_approved ? "Desaprobar" : "Aprobar"}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleApproved(rowData);
            }}
            aria-label={
              rowData.is_approved ? "Desaprobar asesor" : "Aprobar asesor"
            }
            className={`${classes.iconBtn} ${
              rowData.is_approved ? classes.approved : classes.rejected
            }`}
          >
            {rowData.is_approved ? (
              <CheckCircleOutlineIcon fontSize="small" />
            ) : (
              <CancelIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      ),
      width: "7%",
      editable: "never",
    },
    {
      title: "Activo",
      field: "is_public",
      filtering: false,
      render: (rowData) => (
        <Tooltip title={rowData.is_public ? "Desactivar" : "Activar"}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleToggleActive(rowData);
            }}
            aria-label={rowData.is_public ? "Desactivar" : "Activar"}
            className={`${classes.iconBtn} ${
              rowData.is_public ? classes.approved : classes.rejected
            }`}
          >
            {rowData.is_public ? (
              <CheckCircleOutlineIcon fontSize="small" />
            ) : (
              <CancelIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      ),
      width: "7%",
      editable: "never",
    },
  ];

  return (
    <div>
      <DeleteConfirmationDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />
      <MaterialTable
        title={""}
        columns={columns}
        data={salesPeople as SalesPeople[]}
        components={{}}
        onRowClick={(evt, rowData) => {
          const row = Array.isArray(rowData) ? rowData[0] : rowData;
          if (row) handleOpenDetails(row as SalesPeople);
        }}
        actions={[
          {
            icon: () => <DeleteIcon style={{ color: "#ff5630" }} />,
            tooltip: "Eliminar asesor",
            onClick: (event, rowData) => {
              // Avoid triggering onRowClick when clicking the icon
              event?.stopPropagation?.();
              const row = Array.isArray(rowData) ? rowData[0] : rowData;
              if (!row) return;
              setConfirmDialog({
                isOpen: true,
                title: "Confirmar eliminación",
                subTitle: "¿Estás seguro de que deseas eliminar este asesor?",
                onConfirm: async () => {
                  await handleDeleteSalesPerson(
                    (row as SalesPeople).salesperson_id
                  );
                  setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
                },
              });
            },
          },
        ]}
        isLoading={isLoadingSalesPeople}
        options={{
          actionsColumnIndex: -1,
          padding: "dense",
          headerStyle: {
            backgroundColor: "#f4f6f8",
            color: "#637381",
            fontSize: "14px",
            fontWeight: 600,
            height: "4rem",
          },
          rowStyle: {
            fontSize: "14px",
            fontWeight: 400,
            color: "#1C252E",
          },
          searchFieldStyle: {
            marginBlock: "1rem",
          },
          searchFieldVariant: "outlined",
          filtering: true,
        }}
        localization={materialTableES}
      />
      <SalesPersonDetailDialog
        open={openDetail}
        salesPerson={selected}
        onClose={handleCloseDetails}
      />
    </div>
  );
}

export default SalesPersonTable;
